import { useState } from "react";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, MapPin, ArrowUpRight, FacebookLogo, InstagramLogo, LinkedinLogo, Star, ChatCircleText, CalendarCheck } from "@phosphor-icons/react";
import { PageHero } from "@/components/site/PageHero";
import { submitContact, submitQuote } from "@/lib/api";
import { AttachmentUploader } from "@/components/site/AttachmentUploader";
import { BUSINESS, SERVICES } from "@/lib/data";

const field =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-600 focus:border-navy-600 transition";
const label = "block text-sm font-semibold text-navy-900 mb-2";

const TIMES = ["Morning (8am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–7pm)", "Flexible"];

export default function Contact() {
  const [tab, setTab] = useState("quote");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [contactFiles, setContactFiles] = useState([]);
  const [quoteFiles, setQuoteFiles] = useState([]);

  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [quote, setQuote] = useState({
    name: "", email: "", phone: "", service: SERVICES[0].title,
    preferred_date: "", preferred_time: TIMES[0], address: "", details: "",
  });

  const handleContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact({ ...contact, attachments: contactFiles });
      toast.success("Message sent! We'll be in touch shortly.");
      setContact({ name: "", email: "", phone: "", message: "" });
      setContactFiles([]);
    } catch {
      toast.error("Something went wrong. Please call us at " + BUSINESS.phone);
    } finally {
      setLoading(false);
    }
  };

  const handleQuote = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitQuote({ ...quote, attachments: quoteFiles });
      toast.success("Request received! We'll confirm your booking soon.");
      setQuote({ ...quote, name: "", email: "", phone: "", preferred_date: "", address: "", details: "" });
      setQuoteFiles([]);
    } catch {
      toast.error("Something went wrong. Please call us at " + BUSINESS.phone);
    } finally {
      setLoading(false);
    }
  };

  const socials = [
    { icon: FacebookLogo, href: BUSINESS.socials.facebook },
    { icon: InstagramLogo, href: BUSINESS.socials.instagram },
    { icon: LinkedinLogo, href: BUSINESS.socials.linkedin },
    { icon: Star, href: BUSINESS.socials.yelp },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        lines={["Let's make", "a plan."]}
        subtitle="Request a quote, book a service, or just say hello. We keep an open line and respond fast."
      />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 grid lg:grid-cols-[0.85fr_1.15fr] gap-12">
        {/* Info */}
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">Get in touch</span>
          <h2 className="font-display font-black text-4xl tracking-tight text-navy-900 mt-3 leading-[1.1]">
            Serving {BUSINESS.area}.
          </h2>
          <p className="text-slate-600 mt-4">
            Prefer to talk it through? Give us a call — we're easy to reach and happy to help.
          </p>

          <div className="mt-10 space-y-4">
            <a href={BUSINESS.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-navy-900 text-white grid place-items-center group-hover:bg-navy-600 transition-colors">
                <Phone weight="fill" size={20} />
              </span>
              <div>
                <div className="text-sm text-slate-500">Call us</div>
                <div className="font-bold text-navy-900 text-lg">{BUSINESS.phone}</div>
              </div>
            </a>
            <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-navy-900 text-white grid place-items-center group-hover:bg-navy-600 transition-colors">
                <EnvelopeSimple weight="fill" size={20} />
              </span>
              <div>
                <div className="text-sm text-slate-500">Email us</div>
                <div className="font-bold text-navy-900 text-lg break-all">{BUSINESS.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-full bg-navy-900 text-white grid place-items-center">
                <MapPin weight="fill" size={20} />
              </span>
              <div>
                <div className="text-sm text-slate-500">Based in</div>
                <div className="font-bold text-navy-900 text-lg">{BUSINESS.location}</div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-10">
            {socials.map((s, i) => (
              <a
                key={i}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                className="w-11 h-11 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-navy-900 hover:text-white transition-colors"
              >
                <s.icon size={20} weight="fill" />
              </a>
            ))}
          </div>
        </div>

        {/* Form card */}
        <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 md:p-10">
          <div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-200/60 rounded-full mb-8">
            <button
              data-testid="tab-quote"
              onClick={() => setTab("quote")}
              className={`flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm transition-colors ${
                tab === "quote" ? "bg-navy-900 text-white" : "text-navy-900 hover:bg-white/60"
              }`}
            >
              <CalendarCheck weight="fill" size={18} /> Book / Get a Quote
            </button>
            <button
              data-testid="tab-message"
              onClick={() => setTab("message")}
              className={`flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm transition-colors ${
                tab === "message" ? "bg-navy-900 text-white" : "text-navy-900 hover:bg-white/60"
              }`}
            >
              <ChatCircleText weight="fill" size={18} /> Send a Message
            </button>
          </div>

          {tab === "quote" ? (
            <form onSubmit={handleQuote} data-testid="quote-form" className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={label}>Full name</label>
                  <input required data-testid="quote-name" className={field} placeholder="Jane Smith"
                    value={quote.name} onChange={(e) => setQuote({ ...quote, name: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Phone</label>
                  <input required data-testid="quote-phone" className={field} placeholder="(843) 000-0000"
                    value={quote.phone} onChange={(e) => setQuote({ ...quote, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={label}>Email</label>
                <input required type="email" data-testid="quote-email" className={field} placeholder="you@email.com"
                  value={quote.email} onChange={(e) => setQuote({ ...quote, email: e.target.value })} />
              </div>
              <div>
                <label className={label}>Service needed</label>
                <select required data-testid="quote-service" className={field}
                  value={quote.service} onChange={(e) => setQuote({ ...quote, service: e.target.value })}>
                  {SERVICES.map((s) => <option key={s.slug}>{s.title}</option>)}
                  <option>Other / Not sure</option>
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={label}>Preferred date</label>
                  <input type="date" data-testid="quote-date" className={field}
                    value={quote.preferred_date} onChange={(e) => setQuote({ ...quote, preferred_date: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Preferred time</label>
                  <select data-testid="quote-time" className={field}
                    value={quote.preferred_time} onChange={(e) => setQuote({ ...quote, preferred_time: e.target.value })}>
                    {TIMES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={label}>Service address</label>
                <input data-testid="quote-address" className={field} placeholder="Street, City, SC"
                  value={quote.address} onChange={(e) => setQuote({ ...quote, address: e.target.value })} />
              </div>
              <div>
                <label className={label}>Project details</label>
                <textarea required rows={4} data-testid="quote-details" className={field} placeholder="Tell us what you need done…"
                  value={quote.details} onChange={(e) => setQuote({ ...quote, details: e.target.value })} />
              </div>
              <div>
                <label className={label}>Photos or video of the job</label>
                <AttachmentUploader
                  testId="quote-uploader"
                  attachments={quoteFiles}
                  setAttachments={setQuoteFiles}
                  onUploadingChange={setUploading}
                />
              </div>
              <button
                type="submit"
                disabled={loading || uploading}
                data-testid="quote-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-4 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60"
              >
                {uploading ? "Uploading…" : loading ? "Sending…" : "Request my quote"} <ArrowUpRight weight="bold" />
              </button>
            </form>
          ) : (
            <form onSubmit={handleContact} data-testid="contact-form" className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={label}>Full name</label>
                  <input required data-testid="contact-name" className={field} placeholder="Jane Smith"
                    value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Phone</label>
                  <input data-testid="contact-phone-input" className={field} placeholder="(843) 000-0000"
                    value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={label}>Email</label>
                <input required type="email" data-testid="contact-email-input" className={field} placeholder="you@email.com"
                  value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              </div>
              <div>
                <label className={label}>Message</label>
                <textarea required rows={6} data-testid="contact-message" className={field} placeholder="How can we help?"
                  value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
              </div>
              <div>
                <label className={label}>Photos or video (optional)</label>
                <AttachmentUploader
                  testId="contact-uploader"
                  attachments={contactFiles}
                  setAttachments={setContactFiles}
                  onUploadingChange={setUploading}
                />
              </div>
              <button
                type="submit"
                disabled={loading || uploading}
                data-testid="contact-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-4 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60"
              >
                {uploading ? "Uploading…" : loading ? "Sending…" : "Send message"} <ArrowUpRight weight="bold" />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
